/**
 * AbstractHSMSigner - Abstract class for HSM (Hardware Security Module) signing
 *
 * HSM là loại ký số sử dụng server trung tâm.
 * Private key được lưu trên server HSM, client gửi hash lên để ký.
 */

import { BaseSignatureProvider } from './ISignatureProvider';
import {
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../types';

export interface HSMServerConfig {
  /** URL của HSM server */
  serverUrl: string;
  /** API key hoặc token xác thực */
  apiKey?: string;
  /** Client certificate (nếu cần mTLS) */
  clientCert?: string;
  /** Timeout (ms) */
  timeout?: number;
}

/**
 * Abstract HSM Signer
 * Các concrete class (ViettelHSMSigner, VNPTHSMSigner, etc.) sẽ extend class này
 */
export abstract class AbstractHSMSigner extends BaseSignatureProvider {
  readonly signType = SignType.HSM;
  abstract readonly signProvider: SignProvider;
  abstract readonly displayName: string;

  protected config: HSMServerConfig;
  protected authToken: string = '';

  constructor(config?: Partial<HSMServerConfig>) {
    super();
    this.config = {
      serverUrl: config?.serverUrl || '',
      apiKey: config?.apiKey,
      clientCert: config?.clientCert,
      timeout: config?.timeout || 60000,
    };
  }

  /**
   * Xác thực với HSM server
   * Override nếu provider cần xác thực đặc biệt
   */
  async initialize(): Promise<boolean> {
    try {
      // Basic health check
      const isHealthy = await this.checkServerHealth();
      if (!isHealthy) {
        console.error(`HSM Server ${this.displayName} không phản hồi`);
        return false;
      }

      // Authenticate if needed
      if (this.config.apiKey) {
        this.authToken = await this.authenticate();
      }

      this._isReady = true;
      return true;
    } catch (error) {
      console.error('HSM initialization failed:', error);
      return false;
    }
  }

  /**
   * Xác thực với server (nếu cần)
   */
  protected abstract authenticate(): Promise<string>;

  /**
   * Kiểm tra server health
   */
  async checkServerHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.config.serverUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Tạo request headers với auth token
   */
  protected getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey;
    }

    return headers;
  }

  abstract getCertificateInfo(): Promise<CertificateInfo | null>;
  abstract signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult>;

  /**
   * HSM thường hỗ trợ batch signing native
   * Override trong concrete class với implementation cụ thể
   */
  abstract signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult>;

  dispose(): void {
    this.authToken = '';
    super.dispose();
  }
}
